"use client"

import { Suspense, useEffect, useState, type ComponentProps, type Dispatch, type SetStateAction } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import clsx from "clsx"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { useLocale } from "~/lib/i18n/hooks/useLocale"
import { PRODUCT_CUSTOM_ATTRIBUTES } from "~/lib/shopify/constants"
import { useAddToCart } from "~/lib/shopify/hooks/useAddToCart"
import type { TVariant } from "~/lib/shopify/serializers/serialize-variants"
import serializePrice from "~/data/price/serializer"
import useGetCart from "~/hooks/cart/useGetCart"
import { AddToCartButton } from "~/components/ui/AddToCartButton"
import {
  PANEL_SIZE_AUTO_OPEN_PARAM,
  UNIVERSAL_COLOR_OPTION_VALUE,
} from "~/components/ui/Product/ProductHeader/_data/constants"
import type { BundleSelectsStates } from "~/components/ui/Product/ProductHeader/_data/serialize-bundle-selector"
import type { TProductHeaderBundle } from "~/components/ui/Product/ProductHeader/_data/serializer"
import { isProductHeaderWithEmbroidery } from "~/components/ui/Product/ProductHeader/_data/utils/is-product-header-with-embroidery"
import { ColorsSelector } from "~/components/ui/Product/ProductHeader/ColorsSelector"
import { PanelEmbroideryTrigger } from "~/components/ui/Product/ProductHeader/PanelEmbroideryTrigger"
import { Alma } from "~/components/ui/Product/ProductHeader/ProductDetails/Alma"
import { BundleOptionsSelector } from "~/components/ui/Product/ProductHeader/ProductDetails/BundleSelector/BundleOptionsSelector/index.client"
import { findVariantFromSelectedOptions } from "~/components/ui/Product/ProductHeader/ProductDetails/BundleSelector/utils/find-variant-from-selected-options"
import { getSelectDefaultValues } from "~/components/ui/Product/ProductHeader/ProductDetails/BundleSelector/utils/get-select-default-values"
import { serializeBundleLineItem } from "~/components/ui/Product/ProductHeader/ProductDetails/BundleSelector/utils/serialize-bundle-line-item"
import { DeliveryEstimations } from "~/components/ui/Product/ProductHeader/ProductDetails/DeliveryEstimations"
import ProductFomo from "~/components/ui/Product/ProductHeader/ProductDetails/ProductSelector/ProductFomo"
import RoundedCta from "~/components/ui/RoundedCta"
import { Stack } from "~/components/abstracts/Stack"
import { Cart } from "~/components/globals/Cart"
import { getCrossSellProductLineItem } from "~/components/globals/Cart/components/CrossSell/_data/serializeCrossSellProductCard"
import CrossSellProductCardCheckbox from "~/components/globals/Cart/components/CrossSell/CrossSellProductCardCheckbox"
import { ClientViewItem } from "~/providers/GTMTrackingProvider/ClientViewItem"
import { serializeItemIdFromProductInfos } from "~/providers/GTMTrackingProvider/constants"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { usePanel } from "~/managers/PanelManager"
import getAlternateUrl from "~/utils/alternate-url"
import { objectValues } from "~/utils/object-values"

import { sprinkles } from "~/styles/sprinkles.css"
import { hideFrom, showFrom } from "~/styles/utils/show-hide"

type BundleSelectorProps = PropsWithClassName<{
  productHeader: TProductHeaderBundle
  children: {
    sizeChartPanelTrigger: React.ReactNode
  }
  setCurrentOptions?: (newOptions: Nullish<string[]>) => void
  currentOptions?: Nullish<string[]>
}>

export function BundleSelector({
  className,
  productHeader,
  children,
  currentOptions,
  setCurrentOptions,
}: BundleSelectorProps) {
  const locale = useLocale()
  const t = useTranslate()
  const pathname = usePathname()

  const { cart } = useGetCart()
  const { add } = usePanel()
  const { mutate: addCartLines, isLoading } = useAddToCart()

  const [isShownMobile, setIsShownMobile] = useState<boolean>(false)

  // Default options
  const reducedDefaultValues = getSelectDefaultValues(currentOptions, productHeader.defaultBundleOptions)

  // States
  const [currentVariant, setCurrentVariant] = useState<Nullish<TVariant>>(
    findVariantFromSelectedOptions(reducedDefaultValues, productHeader.bundleVariants)
  )

  const [crossSellCtaElement, setCrossSellCtaElement] =
    useState<Parameters<NonNullable<ComponentProps<typeof CrossSellProductCardCheckbox>["onChange"]>>[0]>(null)

  // Line items
  const crossSellCtaLineItem =
    crossSellCtaElement && productHeader?.crossSellCta?.crossSellProductCard?.product
      ? getCrossSellProductLineItem({
          product: productHeader?.crossSellCta?.crossSellProductCard?.product,
          index: cart && cart.lines ? cart.lines.length + 1 : 0,
          pathname,
          additionalAttributes: [
            { key: PRODUCT_CUSTOM_ATTRIBUTES.IS_FROM_PRE_CTA_CROSS_SELL, value: "true" },
            {
              key: PRODUCT_CUSTOM_ATTRIBUTES.COLLECTION_IDS,
              value: JSON.stringify(
                productHeader?.crossSellCta?.crossSellProductCard?.product?.collections.map(
                  (collection) => collection.id
                )
              ),
            },
          ],
          additionalTrackingData: { from_pre_cta_cross_sell: true },
          ...crossSellCtaElement,
        })
      : null

  const bundleLineItem = serializeBundleLineItem({
    currentVariant,
    product: {
      id: productHeader.id,
      collectionIds: productHeader.collectionIds,
      color: productHeader.currentColor,
      bundleOptions: productHeader.bundleOptions,
      collection: productHeader.collection,
      title: productHeader.title,
    },
    productUrl: getAlternateUrl(pathname),
    cartLength: cart && cart.lines ? cart.lines.length : 0,
  })

  // Events
  const onChange = (bundleSelectsStates: BundleSelectsStates) => {
    const foundVariant = findVariantFromSelectedOptions(bundleSelectsStates, productHeader.bundleVariants)

    if (foundVariant) {
      setCurrentVariant(foundVariant)
      setCurrentOptions?.(objectValues(bundleSelectsStates))
    }
  }

  const onClickAddToCart = () => {
    const linesToAdd = [bundleLineItem, crossSellCtaLineItem]?.filter(Boolean)

    if (linesToAdd?.length > 0) {
      addCartLines(linesToAdd, {
        onSuccess: () => {
          add(<Cart />)
        },
      })
    }
  }

  // Price and components
  const price = serializePrice({
    locale,
    currencyCode: currentVariant?.unserializedPrice?.currencyCode,
    amount:
      (crossSellCtaElement?.variant?.unserializedPrice?.amount ?? 0) + (currentVariant?.unserializedPrice?.amount ?? 0),
  })

  const crossSellCta = productHeader?.crossSellCta?.crossSellProductCard && (
    <CrossSellProductCardCheckbox
      item={productHeader?.crossSellCta?.crossSellProductCard}
      selectedOptions={
        productHeader?.bundleVariants?.[productHeader?.crossSellCta?.optionIndex]?.selectedOptions ??
        productHeader?.bundleVariants?.[0]?.selectedOptions ??
        undefined
      }
      customTitle={productHeader?.crossSellCta?.customTitle}
      customImage={productHeader?.crossSellCta?.customImage}
      theme="grey"
      size="small"
      onChange={setCrossSellCtaElement}
      selectorsOrder={["size"]}
    />
  )

  const innerContent = (
    <>
      <BundleOptionsSelector
        bundleOptions={productHeader?.bundleOptions}
        defaultValues={reducedDefaultValues}
        onChange={onChange}
      />
      <div
        className={sprinkles({
          flexDirection: "column",
          gap: 25,
          display: "flex",
          mT: 20,
          mX: { mobile: 10, desktop: 0 },
        })}
      >
        {children?.sizeChartPanelTrigger ? children.sizeChartPanelTrigger : null}

        <Stack gap={12} direction="column" sprinklesCss={{ mB: 20 }}>
          {productHeader.globalProduct?.fomo && currentVariant && productHeader?.id && (
            <ProductFomo
              productId={productHeader.id}
              currentVariant={currentVariant}
              {...productHeader.globalProduct?.fomo}
            />
          )}

          {isProductHeaderWithEmbroidery(productHeader) && bundleLineItem && (
            <PanelEmbroideryTrigger
              currentVariant={currentVariant}
              productHeader={productHeader}
              productLineItem={bundleLineItem}
            />
          )}

          {crossSellCta}
          <AddToCartButton
            width="full"
            isLoading={isLoading}
            price={price}
            disabled={!price}
            isOutOfStock={!currentVariant?.availableForSale}
            onClick={onClickAddToCart}
            variantId={currentVariant?.id ?? null}
            popinBackInStock={productHeader?.popInBackInStock}
            productName={`${productHeader.title} - ${currentVariant?.title}`}
          />
          {productHeader.globalProduct?.almaContent && <Alma data={productHeader.globalProduct?.almaContent} />}
          <DeliveryEstimations />
        </Stack>
      </div>
    </>
  )

  const colorsWithDotImage = productHeader?.colorsWithDotImage
  const filteredColors =
    colorsWithDotImage?.filter((color) => color.image && color.value !== UNIVERSAL_COLOR_OPTION_VALUE) ?? []
  const productHasDifferentsVariants = productHeader?.bundleVariants?.length > 1

  return (
    <div className={className}>
      <Suspense>
        <MobileSizePanelParamSetter setIsShownMobile={setIsShownMobile} />
      </Suspense>
      {filteredColors.length > 0 && (
        <Suspense>
          <ColorsSelector
            currentColor={productHeader?.currentColor?.value}
            sprinklesCss={{ pB: 30, pX: 10 }}
            colorsWithDotImage={filteredColors}
            colorTag={productHeader?.colorTag}
            isBundleSelector
            isMobileBundleSizePanelOpen={isShownMobile}
          />
        </Suspense>
      )}

      {/* Desktop content */}
      <div className={clsx(showFrom("tablet", "block"))}>{innerContent}</div>

      {/* Tablet content, thanks Numbered */}
      <div className={clsx(hideFrom("tablet", "block"))}>
        {(isShownMobile || !productHasDifferentsVariants) && innerContent}
        {!isShownMobile && productHasDifferentsVariants && (
          <div className={sprinkles({ mX: { mobile: 10, desktop: 0 } })}>
            <RoundedCta
              size="large"
              onClick={() => {
                setIsShownMobile(true)
              }}
              width="full"
              theme="blue-deep"
            >
              {t("product_select_sizes")}
            </RoundedCta>
          </div>
        )}
      </div>

      {currentVariant && (
        <ClientViewItem
          payload={{
            item_variant: `${currentVariant?.title ?? ""}`,
            item_id: serializeItemIdFromProductInfos(productHeader?.id, currentVariant?.id),
            item_name: productHeader.title ?? "",
            item_sku: currentVariant.sku ?? "",
            item_url: getAlternateUrl(pathname) ?? "",
            item_image_url: currentVariant?.image?.[0]?.src ? `${currentVariant?.image?.[0]?.src}?width=800` : "",
            item_category: productHeader.collection ?? "",
            price: currentVariant?.unserializedPrice?.amount ?? 0,
            compare_at_price: currentVariant?.unserializedCompareAtPrice?.amount ?? 0,
            currency: productHeader.unserializedPrice?.currencyCode ?? "",
            quantity: 1,
          }}
          deps={[currentVariant?.id]}
        />
      )}
    </div>
  )
}

function MobileSizePanelParamSetter({ setIsShownMobile }: { setIsShownMobile: Dispatch<SetStateAction<boolean>> }) {
  const searchParams = useSearchParams()

  const hasSizePanelDefaultOpened = Boolean(searchParams && searchParams?.get(PANEL_SIZE_AUTO_OPEN_PARAM) === "1")

  useEffect(() => {
    setIsShownMobile(hasSizePanelDefaultOpened)
  }, [hasSizePanelDefaultOpened])

  return null
}
